import { recordActivityView } from '../services/activityHistoryService';
import React, { useState, useEffect } from 'react';
import {
  ArrowLeft, Edit, Mail, Phone, Building2, User, Calendar,
  Brain, BookOpen, FileText, MessageSquare, Clock, ExternalLink,
  Mic, PhoneCall, StickyNote, Plus, Activity, CheckCircle,
  AlertCircle, Tag, Briefcase, Link2
} from 'lucide-react';
import type { Contact, Activity as ActivityType } from '../types';
import { supabase, contactsService } from '../services/supabaseService';
import { clientIntelligenceService, KnowledgeSource } from '../services/clientIntelligenceService';
import { fathomService } from '../services/fathomService';
import { FathomMeeting } from '../types/fathom';
import ContactModal from './ContactModal';
import ClientIntelligencePanel from './intelligence/ClientIntelligencePanel';

interface Props {
  contact: Contact;
  onBack: () => void;
  onContactUpdated: () => void;
  onNavigate?: (contact: Contact) => void;
}

const LEAD_STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  nuevo: { label: 'Nuevo', color: 'bg-blue-100 text-blue-800' },
  en_seguimiento: { label: 'En seguimiento', color: 'bg-yellow-100 text-yellow-800' },
  cerrado: { label: 'Cerrado', color: 'bg-green-100 text-green-800' },
  descartado: { label: 'Descartado', color: 'bg-red-100 text-red-800' },
  ya_es_cliente: { label: 'Ya es cliente', color: 'bg-emerald-100 text-emerald-800' },
};

const ClientDetailPage: React.FC<Props> = ({ contact, onBack, onContactUpdated, onNavigate }) => {
  const [activeTab, setActiveTab] = useState<'resumen' | 'actividades' | 'inteligencia' | 'reuniones' | 'notas'>('resumen');
  const [activities, setActivities] = useState<ActivityType[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [agentCalls, setAgentCalls] = useState<any[]>([]);
  const [fathomMeetings, setFathomMeetings] = useState<FathomMeeting[]>([]);
  const [knowledgeSources, setKnowledgeSources] = useState<KnowledgeSource[]>([]);
  const [linkedRecordingIds, setLinkedRecordingIds] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showIntelligencePanel, setShowIntelligencePanel] = useState(false);
  const [parentContact, setParentContact] = useState<Contact | null>(null);
  const [childContacts, setChildContacts] = useState<Contact[]>([]);

  useEffect(() => {
    loadAllData();
  }, [contact.id]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const parentPromise = contact.parentId
        ? contactsService.getById(contact.parentId)
        : Promise.resolve(null);
      const childrenPromise = contact.entityType === 'company'
        ? contactsService.getAll(false).then(rows => rows.filter(r => r.parentId === contact.id))
        : Promise.resolve([]);

      const [activitiesRes, projectsRes, agentCallsRes, sources, linked, parent, children] = await Promise.all([
        supabase.from('activities').select('*').eq('contact_id', contact.id).order('created_at', { ascending: false }).limit(50),
        supabase.from('projects').select('*').eq('client_id', contact.id).order('created_at', { ascending: false }),
        supabase.from('agent_calls').select('*').eq('contact_id', contact.id).order('created_at', { ascending: false }).limit(20),
        clientIntelligenceService.getSources(contact.id),
        clientIntelligenceService.getLinkedRecordingIds(),
        parentPromise,
        childrenPromise,
      ]);

      if (!activitiesRes.error && !projectsRes.error && !agentCallsRes.error) void recordActivityView('contact', contact.id);
      setParentContact(parent);
      setChildContacts(children);

      setActivities((activitiesRes.data || []).map((a: any) => ({
        id: a.id,
        // Antes se leía a.activity_type (columna inexistente); la real es
        // `type`. El fallback a icono genérico "nota_interna" era una
        // consecuencia silenciosa de este bug.
        type: a.type,
        datetime: new Date(a.datetime || a.created_at),
        description: a.description,
        contactId: a.contact_id,
        owner: a.owner || '',
        createdAt: new Date(a.created_at),
      })));
      setProjects(projectsRes.data || []);
      setAgentCalls(agentCallsRes.data || []);
      setKnowledgeSources(sources);
      setLinkedRecordingIds(linked);

      // Load Fathom meetings if configured
      if (fathomService.isConfigured() && contact.email) {
        try {
          const meetings = await fathomService.getMeetings();
          const clientMeetings = meetings.filter(m =>
            m.calendar_invitees?.some((i: any) =>
              i.email?.toLowerCase() === contact.email?.toLowerCase()
            )
          );
          setFathomMeetings(clientMeetings);
        } catch { /* ignore fathom errors */ }
      }
    } catch (err) {
      console.error('Error loading client data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLinkFathomMeeting = async (meeting: FathomMeeting) => {
    try {
      await clientIntelligenceService.linkFathomMeeting(contact.id, meeting);
      setLinkedRecordingIds(prev => new Set([...prev, meeting.recording_id]));
      const sources = await clientIntelligenceService.getSources(contact.id);
      setKnowledgeSources(sources);
    } catch (err: any) {
      alert(err.message || 'Error vinculando reunión');
    }
  };

  const statusConfig = LEAD_STATUS_CONFIG[contact.leadStatus] || LEAD_STATUS_CONFIG.nuevo;

  const tabs = [
    { id: 'resumen' as const, label: 'Resumen', icon: User },
    { id: 'actividades' as const, label: 'Actividades', icon: Activity },
    { id: 'inteligencia' as const, label: 'Inteligencia', icon: Brain },
    { id: 'reuniones' as const, label: 'Reuniones', icon: Mic },
    { id: 'notas' as const, label: 'Notas', icon: StickyNote },
  ];

  return (
    <div className="p-6">
      {/* Back button + Header */}
      <div className="mb-6">
        <button onClick={onBack} className="flex items-center space-x-2 text-sm text-gray-500 hover:text-gray-700 mb-4">
          <ArrowLeft className="w-4 h-4" />
          <span>Volver a Clientes</span>
        </button>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-4">
              <div className={`h-16 w-16 rounded-full flex items-center justify-center ${
                contact.entityType === 'company' ? 'bg-purple-100' : 'bg-indigo-100'
              }`}>
                {contact.entityType === 'company'
                  ? <Building2 className="h-8 w-8 text-purple-600" />
                  : <User className="h-8 w-8 text-indigo-600" />}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{contact.fullName}</h1>
                <div className="flex items-center space-x-4 mt-1 text-sm text-gray-500">
                  {contact.entityType !== 'company' && contact.company && (
                    <span className="flex items-center space-x-1">
                      <Building2 className="w-4 h-4" />
                      <span>{contact.company}</span>
                    </span>
                  )}
                  {contact.role && contact.entityType !== 'company' && (
                    <span className="flex items-center space-x-1">
                      <Briefcase className="w-4 h-4" />
                      <span>{contact.role}</span>
                    </span>
                  )}
                  {contact.entityType !== 'company' && (
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig.color}`}>
                      {statusConfig.label}
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-4 mt-2 text-sm">
                  {contact.email && (
                    <a href={`mailto:${contact.email}`} className="flex items-center space-x-1 text-blue-600 hover:text-blue-800">
                      <Mail className="w-4 h-4" />
                      <span>{contact.email}</span>
                    </a>
                  )}
                  {contact.phone && (
                    <a href={`tel:${contact.phone}`} className="flex items-center space-x-1 text-green-600 hover:text-green-800">
                      <Phone className="w-4 h-4" />
                      <span>{contact.phone}</span>
                    </a>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowIntelligencePanel(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm"
              >
                <Brain className="w-4 h-4" />
                <span>Chat Inteligencia</span>
              </button>
              <button
                onClick={() => setShowEditModal(true)}
                className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
              >
                <Edit className="w-4 h-4" />
                <span>Editar</span>
              </button>
            </div>
          </div>

          {/* Quick stats */}
          <div className="grid grid-cols-5 gap-4 mt-6 pt-6 border-t border-gray-100">
            <QuickStat icon={Activity} label="Actividades" value={activities.length} color="text-blue-600" />
            <QuickStat icon={Briefcase} label="Proyectos" value={projects.length} color="text-purple-600" />
            <QuickStat icon={Mic} label="Reuniones" value={fathomMeetings.length} color="text-green-600" />
            <QuickStat icon={PhoneCall} label="Llamadas IA" value={agentCalls.length} color="text-orange-600" />
            <QuickStat icon={BookOpen} label="Fuentes Indexadas" value={knowledgeSources.filter(s => s.ingestion_status === 'completed').length} color="text-indigo-600" />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-0 px-6">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-4 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : (
            <>
              {activeTab === 'resumen' && (
                <ResumenTab
                  contact={contact}
                  activities={activities}
                  projects={projects}
                  agentCalls={agentCalls}
                  fathomMeetings={fathomMeetings}
                  knowledgeSources={knowledgeSources}
                  parentContact={parentContact}
                  childContacts={childContacts}
                  onNavigate={onNavigate}
                />
              )}
              {activeTab === 'actividades' && (
                <ActividadesTab activities={activities} />
              )}
              {activeTab === 'inteligencia' && (
                <InteligenciaTab
                  contact={contact}
                  sources={knowledgeSources}
                  onOpenChat={() => setShowIntelligencePanel(true)}
                />
              )}
              {activeTab === 'reuniones' && (
                <ReunionesTab
                  fathomMeetings={fathomMeetings}
                  agentCalls={agentCalls}
                  linkedRecordingIds={linkedRecordingIds}
                  onLinkMeeting={handleLinkFathomMeeting}
                />
              )}
              {activeTab === 'notas' && (
                <NotasTab contact={contact} />
              )}
            </>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      <ContactModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSave={async () => {
          setShowEditModal(false);
          onContactUpdated();
        }}
        contact={contact}
      />

      {/* Intelligence Panel */}
      <ClientIntelligencePanel
        isOpen={showIntelligencePanel}
        onClose={() => {
          setShowIntelligencePanel(false);
          loadAllData(); // Refresh sources after potential changes
        }}
        contact={contact}
      />
    </div>
  );
};

// ================================================================
// Quick Stat
// ================================================================
const QuickStat: React.FC<{ icon: React.ElementType; label: string; value: number; color: string }> = ({ icon: Icon, label, value, color }) => (
  <div className="text-center">
    <div className="flex items-center justify-center space-x-1">
      <Icon className={`w-4 h-4 ${color}`} />
      <span className="text-2xl font-bold text-gray-900">{value}</span>
    </div>
    <p className="text-xs text-gray-500 mt-1">{label}</p>
  </div>
);

// ================================================================
// Resumen Tab
// ================================================================
const ResumenTab: React.FC<{
  contact: Contact;
  activities: ActivityType[];
  projects: any[];
  agentCalls: any[];
  fathomMeetings: FathomMeeting[];
  knowledgeSources: KnowledgeSource[];
  parentContact: Contact | null;
  childContacts: Contact[];
  onNavigate?: (contact: Contact) => void;
}> = ({ contact, activities, projects, agentCalls, fathomMeetings, knowledgeSources, parentContact, childContacts, onNavigate }) => {
  const recentActivities = activities.slice(0, 5);
  const recentCalls = agentCalls.slice(0, 3);
  const isCompany = contact.entityType === 'company';

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Info del cliente */}
      <div className="space-y-6">
        <div>
          <h3 className="text-sm font-semibold text-gray-700 uppercase mb-3">Información</h3>
          <div className="bg-gray-50 rounded-lg p-4 space-y-3 text-sm">
            {isCompany ? (
              <>
                {contact.website && (
                  <InfoRow icon={Link2} label="Sitio web" value={
                    <a href={contact.website} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline break-all">
                      {contact.website}
                    </a>
                  } />
                )}
                <InfoRow icon={Calendar} label="Creada" value={contact.createdAt.toLocaleDateString('es-ES')} />
                <InfoRow icon={Clock} label="Última actividad" value={contact.lastActivity.toLocaleDateString('es-ES')} />
              </>
            ) : (
              <>
                <InfoRow icon={Tag} label="Producto de interés" value={contact.productInterest || '-'} />
                <InfoRow icon={User} label="Owner" value={contact.owner || '-'} />
                {parentContact ? (
                  <InfoRow icon={Building2} label="Empresa" value={
                    <button
                      type="button"
                      onClick={() => onNavigate?.(parentContact)}
                      className="text-blue-600 hover:underline"
                    >
                      {parentContact.fullName}
                    </button>
                  } />
                ) : contact.company ? (
                  <InfoRow icon={Building2} label="Empresa" value={contact.company} />
                ) : null}
                <InfoRow icon={Calendar} label="Creado" value={contact.createdAt.toLocaleDateString('es-ES')} />
                <InfoRow icon={Clock} label="Última actividad" value={contact.lastActivity.toLocaleDateString('es-ES')} />
              </>
            )}
          </div>
        </div>

        {isCompany && (
          <div>
            <h3 className="text-sm font-semibold text-gray-700 uppercase mb-3">
              Personas asociadas ({childContacts.length})
            </h3>
            {childContacts.length > 0 ? (
              <div className="space-y-2">
                {childContacts.map(child => (
                  <button
                    key={child.id}
                    type="button"
                    onClick={() => onNavigate?.(child)}
                    className="w-full text-left bg-gray-50 hover:bg-gray-100 rounded-lg p-3 flex items-center gap-3 transition-colors"
                  >
                    <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <User className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{child.fullName}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {child.role || 'Sin cargo'}{child.email ? ` · ${child.email}` : ''}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400">Sin personas asociadas todavía</p>
            )}
          </div>
        )}

        {/* Proyectos */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 uppercase mb-3">Proyectos ({projects.length})</h3>
          {projects.length > 0 ? (
            <div className="space-y-2">
              {projects.map(p => (
                <div key={p.id} className="bg-gray-50 rounded-lg p-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{p.name}</p>
                    {p.description && <p className="text-xs text-gray-500 mt-0.5">{p.description}</p>}
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    p.status === 'active' ? 'bg-green-100 text-green-800' :
                    p.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {p.status === 'active' ? 'Activo' : p.status === 'completed' ? 'Completado' : 'Pausado'}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400">Sin proyectos asociados</p>
          )}
        </div>

        {/* Fuentes indexadas */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 uppercase mb-3">
            Fuentes de Inteligencia ({knowledgeSources.length})
          </h3>
          {knowledgeSources.length > 0 ? (
            <div className="space-y-2">
              {knowledgeSources.slice(0, 5).map(s => (
                <div key={s.id} className="bg-gray-50 rounded-lg p-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{s.title}</p>
                    <p className="text-xs text-gray-500">{s.source_type} · {s.chunk_count} fragmentos</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    s.ingestion_status === 'completed' ? 'bg-green-100 text-green-800' :
                    s.ingestion_status === 'processing' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {s.ingestion_status === 'completed' ? 'Indexado' : s.ingestion_status === 'processing' ? 'Procesando' : 'Pendiente'}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400">Sin fuentes indexadas</p>
          )}
        </div>
      </div>

      {/* Timeline */}
      <div className="space-y-6">
        <div>
          <h3 className="text-sm font-semibold text-gray-700 uppercase mb-3">Actividad Reciente</h3>
          {recentActivities.length > 0 ? (
            <div className="space-y-3">
              {recentActivities.map(a => (
                <div key={a.id} className="flex items-start space-x-3">
                  <div className={`p-1.5 rounded-lg flex-shrink-0 ${
                    a.type === 'reunion' ? 'bg-blue-100' :
                    a.type === 'llamada' ? 'bg-green-100' :
                    a.type === 'correo' ? 'bg-red-100' :
                    'bg-gray-100'
                  }`}>
                    {a.type === 'reunion' ? <Calendar className="w-4 h-4 text-blue-600" /> :
                     a.type === 'llamada' ? <PhoneCall className="w-4 h-4 text-green-600" /> :
                     a.type === 'correo' ? <Mail className="w-4 h-4 text-red-600" /> :
                     <StickyNote className="w-4 h-4 text-gray-600" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900">{a.description || a.type}</p>
                    <p className="text-xs text-gray-400">{a.datetime.toLocaleDateString('es-ES')} · {a.owner}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400">Sin actividades registradas</p>
          )}
        </div>

        {/* Llamadas ElevenLabs */}
        {recentCalls.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-gray-700 uppercase mb-3">Llamadas IA Recientes</h3>
            <div className="space-y-2">
              {recentCalls.map(call => (
                <div key={call.id} className="bg-orange-50 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <PhoneCall className="w-4 h-4 text-orange-600" />
                      <span className="text-sm font-medium text-gray-900">{call.agent_name || 'Llamada IA'}</span>
                    </div>
                    <span className="text-xs text-gray-500">
                      {call.duration_seconds ? `${Math.round(call.duration_seconds / 60)}min` : ''}
                    </span>
                  </div>
                  {call.summary && <p className="text-xs text-gray-600 mt-1 line-clamp-2">{call.summary}</p>}
                  <p className="text-xs text-gray-400 mt-1">
                    {call.channel} · {new Date(call.started_at || call.created_at).toLocaleDateString('es-ES')}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Reuniones Fathom recientes */}
        {fathomMeetings.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-gray-700 uppercase mb-3">Reuniones Fathom</h3>
            <div className="space-y-2">
              {fathomMeetings.slice(0, 3).map(m => (
                <div key={m.recording_id} className="bg-blue-50 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Mic className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-medium text-gray-900">{m.title}</span>
                    </div>
                    {m.url && (
                      <a href={m.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {new Date(m.recording_start_time || m.created_at).toLocaleDateString('es-ES')} · {m.calendar_invitees?.length || 0} participantes
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const InfoRow: React.FC<{ icon: React.ElementType; label: string; value: React.ReactNode }> = ({ icon: Icon, label, value }) => (
  <div className="flex items-center justify-between">
    <span className="flex items-center space-x-2 text-gray-500">
      <Icon className="w-4 h-4" />
      <span>{label}</span>
    </span>
    <span className="text-gray-900 font-medium text-right">{value}</span>
  </div>
);

// ================================================================
// Actividades Tab
// ================================================================
const ActividadesTab: React.FC<{ activities: ActivityType[] }> = ({ activities }) => {
  if (activities.length === 0) {
    return (
      <div className="text-center py-12">
        <Activity className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">No hay actividades registradas para este cliente</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {activities.map(a => (
        <div key={a.id} className="flex items-start space-x-4 bg-gray-50 rounded-lg p-4">
          <div className={`p-2 rounded-lg flex-shrink-0 ${
            a.type === 'reunion' ? 'bg-blue-100' :
            a.type === 'llamada' ? 'bg-green-100' :
            a.type === 'correo' ? 'bg-red-100' :
            'bg-gray-100'
          }`}>
            {a.type === 'reunion' ? <Calendar className="w-5 h-5 text-blue-600" /> :
             a.type === 'llamada' ? <PhoneCall className="w-5 h-5 text-green-600" /> :
             a.type === 'correo' ? <Mail className="w-5 h-5 text-red-600" /> :
             <StickyNote className="w-5 h-5 text-gray-600" />}
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-900 capitalize">{a.type.replace('_', ' ')}</span>
              <span className="text-xs text-gray-400">
                {a.datetime.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}
              </span>
            </div>
            <p className="text-sm text-gray-600 mt-1">{a.description}</p>
            {a.owner && <p className="text-xs text-gray-400 mt-1">por {a.owner}</p>}
          </div>
        </div>
      ))}
    </div>
  );
};

// ================================================================
// Inteligencia Tab
// ================================================================
const InteligenciaTab: React.FC<{
  contact: Contact;
  sources: KnowledgeSource[];
  onOpenChat: () => void;
}> = ({ contact, sources, onOpenChat }) => {
  const completedSources = sources.filter(s => s.ingestion_status === 'completed');

  return (
    <div className="space-y-6">
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-6 text-center">
        <Brain className="w-12 h-12 text-indigo-400 mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Inteligencia del Cliente</h3>
        <p className="text-sm text-gray-600 mb-4">
          {completedSources.length > 0
            ? `${completedSources.length} fuentes indexadas. Haz preguntas sobre reuniones, llamadas y documentos de ${contact.fullName}.`
            : 'Añade transcripciones, documentos o notas para empezar a hacer consultas inteligentes.'
          }
        </p>
        <button
          onClick={onOpenChat}
          className="px-6 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium"
        >
          Abrir Chat de Inteligencia
        </button>
      </div>

      {/* Sources summary */}
      {sources.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 uppercase mb-3">Fuentes Disponibles</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {sources.map(s => (
              <div key={s.id} className="bg-gray-50 rounded-lg p-3 flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate">{s.title}</p>
                  <p className="text-xs text-gray-500">
                    {s.source_type} · {s.chunk_count} fragmentos
                    {s.meeting_date && ` · ${new Date(s.meeting_date).toLocaleDateString('es-ES')}`}
                  </p>
                </div>
                <span className={`ml-2 text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${
                  s.ingestion_status === 'completed' ? 'bg-green-100 text-green-800' :
                  s.ingestion_status === 'processing' ? 'bg-blue-100 text-blue-800' :
                  s.ingestion_status === 'failed' ? 'bg-red-100 text-red-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {s.ingestion_status === 'completed' ? 'OK' : s.ingestion_status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ================================================================
// Reuniones Tab
// ================================================================
const ReunionesTab: React.FC<{
  fathomMeetings: FathomMeeting[];
  agentCalls: any[];
  linkedRecordingIds: Set<number>;
  onLinkMeeting: (meeting: FathomMeeting) => void;
}> = ({ fathomMeetings, agentCalls, linkedRecordingIds, onLinkMeeting }) => {
  const [linkingId, setLinkingId] = useState<number | null>(null);

  const handleLink = async (meeting: FathomMeeting) => {
    setLinkingId(meeting.recording_id);
    try {
      await onLinkMeeting(meeting);
    } finally {
      setLinkingId(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Fathom */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 uppercase mb-3">
          Reuniones Fathom ({fathomMeetings.length})
        </h3>
        {fathomMeetings.length > 0 ? (
          <div className="space-y-3">
            {fathomMeetings.map(m => {
              const isLinked = linkedRecordingIds.has(m.recording_id);
              return (
                <div key={m.recording_id} className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1 min-w-0">
                      <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                        <Mic className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900">{m.title}</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {new Date(m.recording_start_time || m.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' })}
                          {' · '}{m.calendar_invitees?.length || 0} participantes
                          {m.recorded_by && ` · Grabado por ${m.recorded_by.name}`}
                        </p>
                        {m.default_summary?.markdown_formatted && (
                          <p className="text-xs text-gray-600 mt-2 line-clamp-2">
                            {m.default_summary.markdown_formatted.substring(0, 200)}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 ml-3 flex-shrink-0">
                      {isLinked ? (
                        <span className="flex items-center space-x-1 text-xs text-emerald-600 bg-emerald-50 px-2 py-1 rounded-lg">
                          <CheckCircle className="w-3 h-3" />
                          <span>Indexado</span>
                        </span>
                      ) : (
                        <button
                          onClick={() => handleLink(m)}
                          disabled={linkingId !== null}
                          className="flex items-center space-x-1 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs hover:bg-indigo-700 disabled:opacity-50"
                        >
                          {linkingId === m.recording_id ? (
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                          ) : (
                            <Link2 className="w-3 h-3" />
                          )}
                          <span>Indexar</span>
                        </button>
                      )}
                      {m.url && (
                        <a href={m.url} target="_blank" rel="noopener noreferrer" className="p-1.5 text-gray-400 hover:text-blue-600">
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-gray-400">No se encontraron reuniones de Fathom con este cliente</p>
        )}
      </div>

      {/* ElevenLabs Calls */}
      {agentCalls.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 uppercase mb-3">
            Llamadas IA ({agentCalls.length})
          </h3>
          <div className="space-y-3">
            {agentCalls.map(call => (
              <div key={call.id} className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <div className="p-2 bg-orange-100 rounded-lg flex-shrink-0">
                    <PhoneCall className="w-5 h-5 text-orange-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-900">{call.agent_name || 'Llamada IA'}</p>
                      <span className="text-xs text-gray-500">
                        {call.duration_seconds ? `${Math.round(call.duration_seconds / 60)} min` : ''}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {call.channel} · {new Date(call.started_at || call.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' })}
                    </p>
                    {call.summary && <p className="text-xs text-gray-600 mt-2">{call.summary}</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {fathomMeetings.length === 0 && agentCalls.length === 0 && (
        <div className="text-center py-12">
          <Mic className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No hay reuniones ni llamadas registradas para este cliente</p>
        </div>
      )}
    </div>
  );
};

// ================================================================
// Notas Tab
// ================================================================
const NotasTab: React.FC<{ contact: Contact }> = ({ contact }) => {
  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-gray-700 uppercase">Notas del Cliente</h3>
      {contact.notes ? (
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{contact.notes}</p>
        </div>
      ) : (
        <div className="text-center py-12">
          <StickyNote className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No hay notas. Edita el cliente para añadir notas.</p>
        </div>
      )}
    </div>
  );
};

export default ClientDetailPage;
